import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { differenceInMinutes, format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { TZDate } from '@date-fns/tz';
import { IconCalendarEvent, IconChevronRight } from '@tabler/icons-react';
import { useProfile } from '@shared/api/useProfile';
import { useAppointment } from '@entities/appointment/api/useAppointment';
import { EntityCard } from './entity-card';
import { canCancelLesson, CANCEL_STUDENT_REASON } from '@utils/lib/canCancelLesson';
import s from './interactive-message.module.scss';

// Карточка занятия: клик по карточке ведёт на страницу занятия.
// Время в card.lines зафиксировано на момент создания — заменяем первую
// строку (дата/время) на время в таймзоне зрителя по starts_at занятия.
// Условия отмены — как в AppointmentModal: status 'active', не позднее
// чем за день до начала (для ученика) либо в течение 30 минут после
// бронирования; преподаватель — всегда.
// Кнопки действий не всплывают на клик по карточке.
const CANCEL_ACTION_KEY = 'cancel';
// Длительность по умолчанию — как в AppointmentModal/OrderModal
const DEFAULT_LESSON_MINUTES = 50;

export const AppointmentEntityCard = ({ message }) => {
  const navigate = useNavigate();
  const { data: profile } = useProfile();
  const entityId = message?.payload?.entity_id;
  const { data: appointment } = useAppointment(entityId, false);

  const isTeacher = profile?.role === 'teacher';
  const timezone = profile?.timezone;

  // Длительность занятия на русском ("50 мин."): считаем по starts_at/ends_at,
  // если их нет — берём дефолт, как в AppointmentModal/OrderModal
  const durationLine = useMemo(() => {
    const { starts_at: startsAt, ends_at: endsAt } = appointment || {};
    let minutes = null;
    if (startsAt && endsAt) {
      const diff = differenceInMinutes(new Date(endsAt), new Date(startsAt));
      if (!Number.isNaN(diff) && diff > 0) minutes = diff;
    }
    if (minutes === null) minutes = DEFAULT_LESSON_MINUTES;
    return `${minutes} мин.`;
  }, [appointment]);

  // Те же условия, что в AppointmentModal (см. canCancelLesson):
  // ученик — не позднее чем за день до начала или в течение 30 минут
  // после бронирования; преподаватель — всегда.
  const canCancel = useMemo(() => {
    if (!appointment) return true; // ещё грузится — не блокируем кнопку
    return canCancelLesson({
      status: appointment.status,
      startsAt: appointment.starts_at,
      createdAt: appointment.created_at,
      timezone,
      isTeacher,
    });
  }, [appointment, timezone, isTeacher]);

  const card = useMemo(() => {
    const base = message.card || {};
    const startsAt = appointment?.starts_at;
    let nextCard = base;

    // Время в таймзоне зрителя + длительность на русском
    if (startsAt) {
      const date = new TZDate(startsAt, timezone);
      if (!Number.isNaN(date.getTime())) {
        const timeLine = format(date, 'd MMMM yyyy, HH:mm', { locale: ru });
        const lines = [...(nextCard.lines || [])];
        if (lines.length > 0) lines[0] = timeLine;
        // Вторая строка карточки — длительность ("1h"), приводим к русскому виду
        if (lines.length > 1) lines[1] = durationLine;
        nextCard = { ...nextCard, lines };
      }
    }

    // Условия отмены как в AppointmentModal: неактивное занятие — без
    // кнопки отмены; ученику недоступно — disabled с пояснением
    return {
      ...nextCard,
      actions: (base.actions || [])
        .filter(
          (action) =>
            action.key !== CANCEL_ACTION_KEY ||
            appointment?.status === 'active'
        )
        .map((action) =>
          action.key === CANCEL_ACTION_KEY && !canCancel
            ? { ...action, disabled: true, disabledReason: CANCEL_STUDENT_REASON }
            : action
        ),
    };
  }, [message.card, appointment, timezone, canCancel, durationLine]);

  if (!message.card) return null;

  const handleClick = () => {
    if (entityId) {
      navigate(`/appointment/${entityId}`);
    }
  };

  return (
    <EntityCard
      card={card}
      message={message}
      icon={<IconCalendarEvent size={20} />}
      headerRight={
        entityId ? <IconChevronRight size={18} className={s.chevron} /> : undefined
      }
      onCardClick={entityId ? handleClick : undefined}
    />
  );
};
